import { useCallback } from 'react';
import { usePlatform } from '../shared/usePlatform';
import { getPlatformConfig } from '../../utils/shared/platformUtils';

/**
 * Mobile-specific haptic feedback hook
 * Provides haptic feedback for various mobile interactions
 */
export const useHapticFeedback = () => {
  const { isMobile } = usePlatform();
  const config = getPlatformConfig('mobile');

  // Check if device supports haptic feedback
  const supportsHaptic = () => {
    return isMobile && 'vibrate' in navigator;
  };

  // Light haptic feedback for button presses
  const hapticButton = useCallback((callback, intensity = 'light') => {
    return (...args) => {
      if (supportsHaptic()) {
        const patterns = {
          light: 20,
          medium: 50,
          heavy: 100,
          success: [50, 50, 50],
          error: [200, 100, 200],
          warning: [100, 50, 100],
        };
        
        navigator.vibrate(patterns[intensity] || patterns.light);
      }
      
      if (callback) {
        callback(...args);
      }
    };
  }, [isMobile]);

  // Success haptic feedback
  const hapticSuccess = useCallback(() => {
    if (supportsHaptic()) {
      navigator.vibrate([50, 50, 50]);
    }
  }, [isMobile]);

  // Error haptic feedback
  const hapticError = useCallback(() => {
    if (supportsHaptic()) {
      navigator.vibrate([200, 100, 200]);
    }
  }, [isMobile]);

  // Warning haptic feedback
  const hapticWarning = useCallback(() => {
    if (supportsHaptic()) {
      navigator.vibrate([100, 50, 100]);
    }
  }, [isMobile]);

  // Navigation haptic feedback
  const hapticNavigation = useCallback(() => {
    if (supportsHaptic()) {
      navigator.vibrate(30);
    }
  }, [isMobile]);

  // Selection haptic feedback
  const hapticSelection = useCallback(() => {
    if (supportsHaptic()) {
      navigator.vibrate(40);
    }
  }, [isMobile]);

  // Long press haptic feedback
  const hapticLongPress = useCallback(() => {
    if (supportsHaptic()) {
      navigator.vibrate([100, 50, 100]);
    }
  }, [isMobile]);

  // Swipe haptic feedback
  const hapticSwipe = useCallback((direction) => {
    if (supportsHaptic()) {
      const patterns = {
        left: [30, 20, 30],
        right: [30, 20, 30],
        up: [20, 30, 20],
        down: [20, 30, 20],
      };
      
      navigator.vibrate(patterns[direction] || 30);
    }
  }, [isMobile]);

  // Custom haptic pattern
  const hapticCustom = useCallback((pattern) => {
    if (supportsHaptic()) {
      navigator.vibrate(pattern);
    }
  }, [isMobile]);

  return {
    // Basic haptic functions
    hapticButton,
    hapticSuccess,
    hapticError,
    hapticWarning,
    
    // Interaction-specific haptics
    hapticNavigation,
    hapticSelection,
    hapticLongPress,
    hapticSwipe,
    hapticCustom,
    
    // Utility functions
    supportsHaptic: supportsHaptic(),
    isMobile,
    config,
  };
};
